'use client';

import { DatePicker } from '@koda/koda-components/molecules/DatePicker';
import NumberField from '@koda/koda-components/molecules/NumberField';
import TextField from '@koda/koda-components/molecules/TextField';
import dayjs from 'dayjs';
import { useTranslations } from 'next-intl';
import { ReactElement } from 'react';
import { LicenseTypes } from '../../../types/licenses';
import { useLicenseBusiness } from '../hooks/useLicenseBusiness';
import { Past5Years } from './consts/dateIntervals';
import {
  createSportsEventsAndTournamentsDefaults,
  SPORTS_EVENTS_AND_TOURNAMENTS_CONTENT_PATH,
  sportsEventsAndTournamentsSchema,
} from './sportsEventsAndTournaments.Schema';

export default function SportsEventsAndTournamentsComponent(): ReactElement {
  const licenseCode = LicenseTypes.SportsEventsAndTournaments;

  const { values, setField, getError, costCalculated, showErrors } = useLicenseBusiness(licenseCode, {
    schema: sportsEventsAndTournamentsSchema,
    defaults: createSportsEventsAndTournamentsDefaults,
  });
  const t = useTranslations(SPORTS_EVENTS_AND_TOURNAMENTS_CONTENT_PATH);

  return (
    <>
      <TextField
        label={t('eventName.label')}
        name="eventName"
        placeholder={t('eventName.placeholder')}
        required
        styleVariant="border"
        disabled={costCalculated}
        type="text"
        value={values.eventName}
        onChange={e => setField('eventName', e.target.value)}
        error={showErrors ? getError('eventName') : undefined}
        data-error-field={!!(showErrors && getError('eventName'))}
        validated={!!values.eventName && !getError('eventName')}
      />

      <DatePicker
        label={t('eventDate.label')}
        name="eventDate"
        placeholder={t('eventDate.placeholder')}
        disabled={costCalculated}
        value={values.eventDate ? new Date(values.eventDate) : undefined}
        onChange={date => setField('eventDate', date ? dayjs(date).format('YYYY-MM-DD') : '')}
        error={showErrors ? getError('eventDate') : undefined}
        data-error-field={!!(showErrors && getError('eventDate'))}
        validated={!!values.eventDate && !getError('eventDate')}
        initialVisibleValue={new Date()}
        interval={Past5Years}
      />

      <NumberField
        label={t('spectatorsCount.label')}
        name="spectatorsCount"
        placeholder={t('spectatorsCount.placeholder')}
        required
        formatThousands
        disabled={costCalculated}
        value={values.spectatorsCount}
        onChange={e => setField('spectatorsCount', parseInt(e.target.value))}
        error={showErrors ? getError('spectatorsCount') : undefined}
        data-error-field={!!(showErrors && getError('spectatorsCount'))}
        validated={!!values.spectatorsCount && !getError('spectatorsCount')}
        min={1}
        sign="positive-only"
        styleVariant="border"
      />
    </>
  );
}
